import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { canAccessPage } from "./lib/permissions";
import { SettingsProvider } from "./context/SettingsContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Kanban from "./pages/Kanban";
import Blocks from "./pages/Blocks";
import StageList from "./pages/StageList";
import Charges from "./pages/Charges";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import Statistics from "./pages/Statistics";
import CleaningAdmin from "./pages/CleaningAdmin";
import Help from "./pages/Help";
import Messages from "./pages/Messages";
import Repairs from "./pages/Repairs";
import SpecialConsiderations from "./pages/SpecialConsiderations";
import Layout from "./layout/Layout";
import { Toaster } from "sonner";
import Profile from "./pages/Profile";
import { ApplicationProvider } from "./context/ApplicationContext";
import { RequireAccess } from "./components/AccessGate";
import ConfigHome from "./pages/config/ConfigHome";
import ConfigLocations from "./pages/config/Locations";
import ConfigRoles from "./pages/config/Roles";
import ConfigMemberships from "./pages/config/Memberships";
import ConfigImport from "./pages/config/ImportData";
import ConfigBranding from "./pages/config/Branding";
import ConfigWorkflow from "./pages/config/Workflow";
import ConfigSharedSettings from "./pages/config/SharedSettings";
import ConfigFoundations from "./pages/config/Foundations";
import ConfigRoomTypes from "./pages/config/RoomTypes";
import OperationalConfig from "./pages/config/OperationalConfig";
import AuditLog from "./pages/config/AuditLog";
import AdministrationBackLink from "./components/AdministrationBackLink";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function KanbanGate({ children }) {
  const { user } = useAuth();
  if (user?.permission_keys?.includes("tar.kanban.read") && user?.allow_kanban !== false) return children;
  return <Navigate to="/" replace />;
}

function AccessDenied() {
  return (
    <div className="bg-white border border-gray-200 p-8 text-center" data-testid="access-denied-page">
      <h1 className="font-chivo font-black text-2xl mb-2">No access</h1>
      <p className="text-sm text-gray-600">You do not have access to the requested page.</p>
    </div>
  );
}

function PageGate({ page, children }) {
  const { user } = useAuth();
  if (canAccessPage(user, page)) return children;
  return <AccessDenied />;
}

function App() {
  return (
    <AuthProvider>
      <ApplicationProvider><SettingsProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/blocks" element={<PageGate page="blocks"><Blocks /></PageGate>} />
              <Route path="/charges" element={<PageGate page="charges"><Charges /></PageGate>} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/repairs" element={<PageGate page="repairs"><Repairs /></PageGate>} />
              <Route path="/special-considerations" element={<PageGate page="special_considerations"><SpecialConsiderations /></PageGate>} />
              <Route path="/help" element={<Help />} />
              <Route path="/stage/:stage" element={<StageList />} />
              <Route path="/kanban" element={<KanbanGate><Kanban /></KanbanGate>} />
              <Route path="/users" element={<AdminOnly><Users /></AdminOnly>} />
              <Route path="/config" element={<RequireAccess lution="config" any={["config.locations.read", "config.portfolios.read", "config.sites.read", "config.roles.read", "config.users.read", "config.imports.read", "config.imports.run", "config.branding.read", "config.workflows.read", "config.repair_sla.read", "config.shared_settings.read", "config.room_types.read", "config.rules.read", "config.flags.read", "config.key_return.read", "config.bulk_actions.read"]}><ConfigHome /></RequireAccess>} />
              <Route path="/config/locations" element={<RequireAccess lution="config" any={["config.locations.read", "config.portfolios.read", "config.sites.read"]}><AdminChild><ConfigLocations /></AdminChild></RequireAccess>} />
              <Route path="/config/access/roles" element={<RequireAccess lution="config" permission="config.roles.read"><AdminChild><ConfigRoles /></AdminChild></RequireAccess>} />
              <Route path="/config/access/users" element={<RequireAccess lution="config" permission="config.users.read"><AdminChild><ConfigMemberships /></AdminChild></RequireAccess>} />
              <Route path="/config/import" element={<RequireAccess lution="config" any={["config.imports.read", "config.imports.run"]}><AdminChild><ConfigImport /></AdminChild></RequireAccess>} />
              <Route path="/config/branding" element={<RequireAccess lution="config" permission="config.branding.read"><AdminChild><ConfigBranding /></AdminChild></RequireAccess>} />
              <Route path="/config/workflows" element={<RequireAccess lution="config" permission="config.workflows.read"><AdminChild><ConfigWorkflow /></AdminChild></RequireAccess>} />
              <Route path="/config/shared-settings" element={<RequireAccess lution="config" permission="config.shared_settings.read"><AdminChild><ConfigSharedSettings /></AdminChild></RequireAccess>} />
              <Route path="/config/foundations" element={<RequireAccess lution="config" permission="config.repair_sla.read"><AdminChild><ConfigFoundations /></AdminChild></RequireAccess>} />
              <Route path="/config/room-types" element={<RequireAccess lution="config" permission="config.room_types.read"><AdminChild><ConfigRoomTypes /></AdminChild></RequireAccess>} />
              <Route path="/config/audit" element={<RequireAccess permission="audit.read"><AdminChild><AuditLog /></AdminChild></RequireAccess>} />
              <Route path="/config/operational" element={<RequireAccess lution="config" any={["config.rules.read","config.flags.read","config.key_return.read","config.bulk_actions.read"]}><AdminChild><OperationalConfig /></AdminChild></RequireAccess>} />
              <Route path="/statistics" element={<PageGate page="statistics"><Statistics /></PageGate>} />
              <Route path="/cleaning-admin" element={<CleaningAdmin />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </SettingsProvider></ApplicationProvider>
    </AuthProvider>
  );
}

export default App;

function AdminChild({children}) { return <div><AdministrationBackLink/>{children}</div>; }
